import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { FwaasStatusTag, type Fwaas } from "@shared/components/fwaas/FwaasData";
import { formatCalendarDate, type VmSpecs } from "kontrolEdge/components/Shared/VmSpecsCard";
import { timeAgo } from "../overview/overviewFormat";
import type { AttentionItem } from "../attention/needsAttention";

/** `12` + `month` → "12 months"; no term → `null`. */
export function formatTerm(term: number | null | undefined, unit: string | null | undefined): string | null {
    if (term == null) return null;
    if (!unit) return String(term);
    return `${term} ${unit}${term === 1 ? "" : "s"}`;
}

/** ["60-1B7E2F", "FortiGate-VM64-HV", "2 vCPU", "12 months, renews Jun 10, 2028"] — what is known. */
export function serviceMeta(service: Fwaas, vm: VmSpecs | null): string[] {
    const term = formatTerm(service.term, service.termUnit);
    const renews = service.renewDate ? `renews ${formatCalendarDate(service.renewDate)}` : null;
    return [
        service.subscriptionId,
        vm?.platform,
        vm?.vcpu != null ? `${vm.vcpu} vCPU` : null,
        [term, renews].filter(Boolean).join(", ") || null,
    ].filter((part): part is string => Boolean(part));
}

/**
 * The top of the Virtual Firewall detail (CTX-1208): whose it is, its state, what was
 * contracted, and — on the right — the most serious thing that needs attention and how
 * fresh the firewall's reading is. Review items never make the pill: they are not a
 * state of the firewall.
 */
export default function FwaasHeader({
    service,
    vm,
    attention,
    readAt,
    onAttentionClick,
}: {
    service: Fwaas;
    vm: VmSpecs | null;
    /** The page's Observations list, most serious first. */
    attention: AttentionItem[];
    /** When the firewall was read; `null` before the first reading or when it failed. */
    readAt: string | null;
    onAttentionClick: (item: AttentionItem) => void;
}) {
    const top = attention.find((item) => item.severity !== "review") ?? null;
    return (
        <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
                {service.customer?.name ? <p className="text-sm text-semantic-text-secondary">{service.customer.name}</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-lg font-semibold">{service.name}</h2>
                    <FwaasStatusTag status={service.status} />
                </div>
                <p className="text-sm text-semantic-text-secondary">
                    {serviceMeta(service, vm).map((part, i) => (
                        <React.Fragment key={part}>
                            {i > 0 ? " · " : null}
                            <span>{part}</span>
                        </React.Fragment>
                    ))}
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                {top && (
                    // A real button around the tag: the pill is reached by keyboard too.
                    <button type="button" className="rounded-md" onClick={() => onAttentionClick(top)}>
                        <GenericTag label={top.headline} variant={top.severity === "critical" ? "error" : "warning"} size="small" />
                    </button>
                )}
                {readAt && <span className="text-sm text-semantic-text-secondary">Updated {timeAgo(readAt)}</span>}
            </div>
        </header>
    );
}
